import { useQuery } from '@apollo/client';
import {
  Alert,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Spin,
  Statistic,
  Table,
  Typography,
} from 'antd';
import React, { useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import {
  OnboardingTaskRoles,
  PartnerOnboardingStages,
  PartnerOnboardingStatuses,
  extractPartnerOnboardingStats,
  partnerOnboardingStatsQuery,
  partnerTypeLabel,
} from './constants';

const { Text } = Typography;

// Categorical hues are assigned in fixed order and never cycled — a 9th series folds
// into "Other" instead of reusing a hue, so one colour always means one thing.
const CATEGORICAL = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948',
];
const MAX_SERIES = 8;
// The monthly trend focuses on the recent stretch (business ask: last 6 months).
const RECENT_MONTHS = 6;
const OTHER_KEY = '__other__';
const OTHER_LABEL = 'Other';
const OTHER_COLOR = '#8c8c8c';
// Server-side sentinel for automated work — a bucket, not a person.
const SYSTEM_USER_ID = '__system__';
// Status colours are reserved for state and never reused as a series colour.
const STATUS_COLOR = {
  IN_PROGRESS: '#2a78d6',
  COMPLETE: '#008300',
  CANCELLED: '#e34948',
};
const REMAINING_COLOR = '#b0afab';
const GRID_COLOR = '#ebebe8';
const AXIS_TICK = { fontSize: 12, fill: '#52514e' };
const SURFACE = '#ffffff';
const CHART_HEIGHT = 300;

const seriesColor = (key, index) =>
  key === OTHER_KEY ? OTHER_COLOR : CATEGORICAL[index] || OTHER_COLOR;

/**
 * Rank series by total and keep the top `max`; everything else folds into a single
 * "Other" series. Returns the ordered keys plus a fold() that maps a raw key to the
 * key it should be drawn under.
 */
function foldSeries(rows, keyOf, valueOf, max = MAX_SERIES) {
  const totals = {};
  (rows || []).forEach((row) => {
    const key = keyOf(row);
    if (key === undefined || key === null) return;
    totals[key] = (totals[key] || 0) + valueOf(row);
  });
  const ranked = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
  const kept = ranked.slice(0, max);
  const keptSet = new Set(kept);
  return {
    keys: ranked.length > max ? [...kept, OTHER_KEY] : kept,
    fold: (key) => (keptSet.has(String(key)) ? String(key) : OTHER_KEY),
  };
}

// Every count on this dashboard is a doorway: drill through to the filtered list of
// partners behind it, or straight to the contact.
const listLink = (params) => {
  // The list page defaults to in-progress when opened directly, but these counts are
  // aggregated over every status -- so each link says so, or the tile would land on a
  // narrower list than the number that was clicked. An explicit `status` still wins.
  const query = new URLSearchParams(
    Object.entries({ status: 'ALL', ...params }).filter(
      ([, value]) => value !== undefined && value !== null,
    ),
  ).toString();
  return `/partner-onboarding${query ? `?${query}` : ''}`;
};

const contactLink = (contactId) =>
  `/contact/view/${contactId}/partner-onboarding`;

function ChartCard({
  title,
  extra,
  isEmpty,
  emptyText,
  height = CHART_HEIGHT,
  children,
}) {
  return (
    <Card title={title} extra={extra} styles={{ body: { paddingTop: 12 } }}>
      {isEmpty ? (
        <div
          style={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
        </div>
      ) : (
        children
      )}
    </Card>
  );
}

function PipelineTiles({ statusCounts }) {
  const byStatus = useMemo(() => {
    const map = {};
    (statusCounts || []).forEach(({ status, count }) => {
      map[status] = count;
    });
    return map;
  }, [statusCounts]);
  const total = Object.values(byStatus).reduce((a, b) => a + b, 0);

  const tiles = [
    {
      key: 'TOTAL',
      label: 'Total Partners',
      value: total,
      color: '#52514e',
      to: listLink({}),
    },
    ...Object.values(PartnerOnboardingStatuses).map((s) => ({
      key: s.key,
      label: s.label,
      value: byStatus[s.key] || 0,
      color: STATUS_COLOR[s.key],
      to: listLink({ status: s.key }),
    })),
  ];

  return (
    <Row gutter={[16, 16]}>
      {tiles.map((tile) => (
        <Col key={tile.key} xs={12} lg={6}>
          <Link to={tile.to} title={`View ${tile.label.toLowerCase()}`}>
            <Card
              size="small"
              hoverable
              style={{ borderTop: `3px solid ${tile.color}` }}
            >
              <Statistic
                title={tile.label}
                value={tile.value}
                valueStyle={{ color: tile.color }}
              />
            </Card>
          </Link>
        </Col>
      ))}
    </Row>
  );
}

function StageProgressChart({ stageTaskCounts }) {
  const data = useMemo(
    () =>
      (stageTaskCounts || []).map(({ stage, completed, total }) => ({
        stage,
        stageLabel: PartnerOnboardingStages[stage]?.label || `Stage ${stage}`,
        completed,
        remaining: Math.max(0, total - completed),
        pct: total ? Math.round((completed / total) * 100) : 0,
      })),
    [stageTaskCounts],
  );

  return (
    <ChartCard
      title="Task Completion by Stage"
      extra={<Text type="secondary">All partners</Text>}
      isEmpty={!data.length}
      emptyText="No onboarding tasks yet"
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 56, left: 8, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={GRID_COLOR}
            horizontal={false}
          />
          <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} />
          <YAxis
            dataKey="stageLabel"
            type="category"
            width={130}
            tick={AXIS_TICK}
          />
          <Tooltip
            formatter={(value, name) => [
              value,
              name === 'completed' ? 'Completed' : 'Remaining',
            ]}
          />
          <Legend
            formatter={(v) => (v === 'completed' ? 'Completed' : 'Remaining')}
          />
          <Bar
            dataKey="completed"
            stackId="s"
            fill={CATEGORICAL[0]}
            stroke={SURFACE}
            strokeWidth={2}
          />
          <Bar
            dataKey="remaining"
            stackId="s"
            fill={REMAINING_COLOR}
            stroke={SURFACE}
            strokeWidth={2}
            radius={[0, 4, 4, 0]}
          >
            <LabelList
              dataKey="pct"
              position="right"
              formatter={(v) => `${v}%`}
              style={{ fontSize: 12, fontWeight: 600, fill: '#52514e' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function NeedsAttentionCard({ bottlenecks }) {
  const b = bottlenecks || {};
  const stalled = useMemo(() => b.stalled || [], [b.stalled]);
  // Column-filter options from the rows present, so RMC staff can narrow to
  // e.g. all stalled Coordinators in one region to work through together.
  const { typeFilters, regionFilters } = useMemo(() => {
    const types = [
      ...new Set(stalled.map((r) => r.partnerType).filter(Boolean)),
    ]
      .sort()
      .map((code) => ({ text: partnerTypeLabel(code), value: code }));
    const regionNames = {};
    stalled.forEach((r) => {
      if (r.region) regionNames[r.region] = r.regionName || r.region;
    });
    const regions = Object.keys(regionNames)
      .sort((x, y) => regionNames[x].localeCompare(regionNames[y]))
      .map((code) => ({ text: regionNames[code], value: code }));
    return { typeFilters: types, regionFilters: regions };
  }, [stalled]);
  return (
    <Card title="Needs Attention" styles={{ body: { paddingTop: 12 } }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={8}>
          <Statistic
            title={`Stalled >${b.stalledAfterDays || 30}d`}
            value={b.stalledCount || 0}
            valueStyle={{ color: STATUS_COLOR.CANCELLED }}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title="Tasks past target"
            value={b.overdueTaskCount || 0}
          />
        </Col>
        <Col xs={8}>
          <Statistic
            title="Oldest in progress"
            value={b.oldestInProgressDays || 0}
            suffix="d"
          />
        </Col>
      </Row>
      {stalled.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nothing is stalled — nice."
        />
      ) : (
        <Table
          dataSource={stalled}
          rowKey="partnerOnboardingId"
          size="small"
          pagination={{ pageSize: 8, hideOnSinglePage: true, size: 'small' }}
          scroll={{ x: 480 }}
          columns={[
            {
              title: 'Partner',
              dataIndex: 'contactName',
              render: (name, row) => {
                if (!name) {
                  return <Text type="secondary">Unnamed contact</Text>;
                }
                // Matches the list/modal rule: the contact read permission is
                // region-scoped, so only link where the caller can actually open it.
                if (!row.canViewContact || !row.contactId) {
                  return name;
                }
                return <Link to={contactLink(row.contactId)}>{name}</Link>;
              },
            },
            {
              title: 'Type',
              dataIndex: 'partnerType',
              width: 130,
              filters: typeFilters,
              onFilter: (value, row) => row.partnerType === value,
              render: (code) =>
                code ? (
                  <Link to={listLink({ partnerType: code })}>
                    {partnerTypeLabel(code)}
                  </Link>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Region',
              dataIndex: 'regionName',
              width: 150,
              filters: regionFilters,
              onFilter: (value, row) => row.region === value,
              render: (name, row) =>
                row.region ? (
                  <Link to={listLink({ region: row.region })}>
                    {name || row.region}
                  </Link>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Idle',
              dataIndex: 'daysIdle',
              width: 80,
              // Tiebreaker matches the backend's stable (-daysIdle, id) order so equal-idle
              // rows don't reshuffle vs. what the server sent.
              sorter: (a, c) =>
                a.daysIdle - c.daysIdle ||
                a.partnerOnboardingId.localeCompare(c.partnerOnboardingId),
              defaultSortOrder: 'descend',
              render: (d) => `${d}d`,
            },
            {
              title: 'Open',
              dataIndex: 'openTasks',
              width: 70,
              sorter: (a, c) => a.openTasks - c.openTasks,
            },
          ]}
        />
      )}
      {stalled.length > 0 && stalled.length < (b.stalledCount || 0) && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {`Showing the ${stalled.length} most idle of ${b.stalledCount} stalled — filter by region or type to narrow.`}
        </Text>
      )}
    </Card>
  );
}

function PendingByStageRoleChart({ pendingTasksByStageRole }) {
  const { data, series } = useMemo(() => {
    const rows = pendingTasksByStageRole || [];
    const { keys, fold } = foldSeries(
      rows,
      (r) => r.role,
      (r) => r.count,
    );
    const byStage = {};
    rows.forEach(({ stage, role, count }) => {
      if (!byStage[stage]) {
        byStage[stage] = {
          stage,
          stageLabel: PartnerOnboardingStages[stage]?.label || `Stage ${stage}`,
        };
      }
      const key = fold(role);
      byStage[stage][key] = (byStage[stage][key] || 0) + count;
    });
    return {
      data: Object.values(byStage).sort((a, b) => a.stage - b.stage),
      series: keys,
    };
  }, [pendingTasksByStageRole]);

  const roleLabel = (key) =>
    key === OTHER_KEY ? OTHER_LABEL : OnboardingTaskRoles[key]?.label || key;

  return (
    <ChartCard
      title="Open Tasks by Stage & Role"
      extra={<Text type="secondary">Excludes automated tasks</Text>}
      isEmpty={!data.length}
      emptyText="No open staff tasks"
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart
          data={data}
          margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={GRID_COLOR}
            vertical={false}
          />
          <XAxis
            dataKey="stageLabel"
            interval="preserveStartEnd"
            tickMargin={8}
            tick={AXIS_TICK}
          />
          <YAxis allowDecimals={false} tick={AXIS_TICK} />
          <Tooltip formatter={(value, name) => [value, roleLabel(name)]} />
          <Legend formatter={roleLabel} />
          {series.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              fill={seriesColor(key, i)}
              stroke={SURFACE}
              strokeWidth={2}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function CompletedByPersonChart({ completedTasksByPersonMonth }) {
  const { data, people } = useMemo(() => {
    const allRows = completedTasksByPersonMonth || [];
    // Window to the most recent RECENT_MONTHS months that have data, then rank
    // contributors within that window so the legend reflects who's active now.
    const recentMonths = new Set(
      [...new Set(allRows.map((r) => r.month))].sort().slice(-RECENT_MONTHS),
    );
    const rows = allRows.filter((r) => recentMonths.has(r.month));
    const totals = {};
    rows.forEach(({ userId, userName, count }) => {
      if (!totals[userId]) totals[userId] = { userId, userName, total: 0 };
      totals[userId].total += count;
    });
    const top = Object.values(totals)
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
    const topIds = new Set(top.map((p) => p.userId));
    const byMonth = {};
    rows.forEach(({ userId, month, count }) => {
      if (!topIds.has(userId)) return;
      if (!byMonth[month]) byMonth[month] = { month };
      byMonth[month][userId] = count;
    });
    return {
      data: Object.values(byMonth).sort((a, b) =>
        a.month.localeCompare(b.month),
      ),
      people: top,
    };
  }, [completedTasksByPersonMonth]);

  const nameOf = (id) => people.find((p) => p.userId === id)?.userName || id;

  return (
    <ChartCard
      title="Tasks Completed per Month"
      extra={
        <Text type="secondary">{`Top contributors · last ${RECENT_MONTHS} months`}</Text>
      }
      isEmpty={!people.length}
      emptyText="No task completions yet"
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart
          data={data}
          margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={GRID_COLOR}
            vertical={false}
          />
          <XAxis dataKey="month" interval={0} tickMargin={8} tick={AXIS_TICK} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} />
          <Tooltip formatter={(value, name) => [value, nameOf(name)]} />
          <Legend formatter={nameOf} />
          {people.map((person, i) => (
            <Line
              key={person.userId}
              dataKey={person.userId}
              stroke={seriesColor(person.userId, i)}
              strokeWidth={2}
              dot={{ r: 4, strokeWidth: 2, stroke: SURFACE }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function PendingByPersonTable({ pendingTasksByPerson }) {
  const data = (pendingTasksByPerson || []).slice(0, 12);
  const max = data.reduce((acc, row) => Math.max(acc, row.count), 0);

  return (
    <Card
      title="Open Tasks by Assigned Staff"
      extra={<Text type="secondary">Tasks on their partners</Text>}
      styles={{ body: { paddingTop: 12 } }}
    >
      {data.length === 0 ? (
        <div
          style={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No staff assigned to any partner yet"
          />
        </div>
      ) : (
        <Table
          dataSource={data}
          rowKey="userId"
          size="small"
          pagination={false}
          scroll={{ y: 260 }}
          columns={[
            {
              title: 'Staff',
              dataIndex: 'userName',
              render: (name, row) =>
                // The system actor is an attribution bucket, not a person to filter by.
                row.userId === SYSTEM_USER_ID ? (
                  name
                ) : (
                  <Link
                    to={listLink({
                      assignedStaffId: row.userId,
                      staffName: name,
                    })}
                  >
                    {name}
                  </Link>
                ),
            },
            {
              title: 'Open tasks',
              dataIndex: 'count',
              width: 190,
              render: (count) => (
                <Progress
                  percent={max ? Math.round((count / max) * 100) : 0}
                  format={() => count}
                  size="small"
                  strokeColor={CATEGORICAL[0]}
                />
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}

function PartnerFlowPerQuarterChart({ quarterFlowCounts }) {
  const data = quarterFlowCounts || [];
  const totals = data.reduce(
    (acc, row) => ({
      started: acc.started + row.started,
      completed: acc.completed + row.completed,
    }),
    { started: 0, completed: 0 },
  );

  return (
    <ChartCard
      title="Onboardings Started vs Completed"
      extra={
        <Text type="secondary">
          {`last 3 years · ${totals.started} started · ${totals.completed} completed`}
        </Text>
      }
      isEmpty={!data.length}
      emptyText="No onboardings yet"
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart
          data={data}
          margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={GRID_COLOR}
            vertical={false}
          />
          {/* Quarter labels are short, so they read straight — rotating them ran the
              text into the legend. preserveStartEnd thins them out instead of
              overlapping once the range grows past a few years. */}
          <XAxis
            dataKey="quarter"
            interval="preserveStartEnd"
            tickMargin={8}
            tick={AXIS_TICK}
          />
          <YAxis allowDecimals={false} tick={AXIS_TICK} />
          <Tooltip
            formatter={(value, name) => [
              value,
              name === 'started' ? 'Started' : 'Completed',
            ]}
          />
          <Legend
            formatter={(v) => (v === 'started' ? 'Started' : 'Completed')}
          />
          {/* Both series are partner counts, so they share one axis. */}
          <Bar dataKey="started" fill={CATEGORICAL[0]} radius={[4, 4, 0, 0]}>
            <LabelList
              dataKey="started"
              position="top"
              style={{ fontSize: 11, fill: '#52514e' }}
            />
          </Bar>
          <Bar dataKey="completed" fill={CATEGORICAL[2]} radius={[4, 4, 0, 0]}>
            <LabelList
              dataKey="completed"
              position="top"
              style={{ fontSize: 11, fill: '#52514e' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function RegionFlowChart({ regionFlowCounts }) {
  const history = useHistory();
  // `regionName` is null for a code the JP region map doesn't know, which would draw an
  // unlabelled (but still clickable) bar — fall back to the raw code like the other panels.
  const data = useMemo(
    () =>
      (regionFlowCounts || []).map((row) => ({
        ...row,
        regionLabel: row.regionName || row.region,
      })),
    [regionFlowCounts],
  );

  return (
    <ChartCard
      title="Started vs Completed by Region"
      extra={<Text type="secondary">Click a region to view its partners</Text>}
      isEmpty={!data.length}
      emptyText="No partners yet"
    >
      <ResponsiveContainer
        width="100%"
        height={Math.max(CHART_HEIGHT, data.length * 56 + 40)}
      >
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 40, left: 8, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={GRID_COLOR}
            horizontal={false}
          />
          <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} />
          <YAxis
            dataKey="regionLabel"
            type="category"
            width={170}
            tick={AXIS_TICK}
          />
          <Tooltip
            formatter={(value, name) => [
              value,
              name === 'started' ? 'Started' : 'Completed',
            ]}
          />
          <Legend
            formatter={(v) => (v === 'started' ? 'Started' : 'Completed')}
          />
          {/* Both series are partner counts, so they share one axis. Same two colours
              as the quarter chart, so the pair reads as one idea. */}
          <Bar
            dataKey="started"
            fill={CATEGORICAL[0]}
            radius={[0, 4, 4, 0]}
            cursor="pointer"
            onClick={(row) => history.push(listLink({ region: row.region }))}
          >
            <LabelList
              dataKey="started"
              position="right"
              style={{ fontSize: 11, fill: '#52514e' }}
            />
          </Bar>
          <Bar
            dataKey="completed"
            fill={CATEGORICAL[2]}
            radius={[0, 4, 4, 0]}
            cursor="pointer"
            onClick={(row) => history.push(listLink({ region: row.region }))}
          >
            <LabelList
              dataKey="completed"
              position="right"
              style={{ fontSize: 11, fill: '#52514e' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function PartnersByTypeCard({ partnerTypeCounts }) {
  const types = (partnerTypeCounts || []).slice(0, 8);
  const isEmpty = !types.length;

  return (
    <Card title="Partners by Type" styles={{ body: { paddingTop: 12 } }}>
      {isEmpty ? (
        <div
          style={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No partners yet"
          />
        </div>
      ) : (
        <Table
          dataSource={types}
          rowKey="partnerType"
          size="small"
          pagination={false}
          showHeader={false}
          style={{ marginTop: 8 }}
          columns={[
            {
              dataIndex: 'partnerType',
              render: (code) => (
                <Link to={listLink({ partnerType: code })}>
                  {partnerTypeLabel(code)}
                </Link>
              ),
            },
            { dataIndex: 'count', width: 70, align: 'right' },
          ]}
        />
      )}
    </Card>
  );
}

function PartnerOnboardingDashboardPage() {
  const { data, loading, error } = useQuery(partnerOnboardingStatsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const stats = extractPartnerOnboardingStats(data);

  return (
    <CSPage
      containerStyle={{ maxWidth: 'unset' }}
      title="Partner Onboarding Dashboard"
    >
      <CSPageHeader title="Partner Onboarding Dashboard" />
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          message="Could not load onboarding stats"
          description={error.message}
        />
      )}
      {stats?.truncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          message="Showing a partial view"
          description="There are more onboardings than this dashboard scans in one pass, so the totals below are incomplete."
        />
      )}
      {loading && !stats ? (
        <Spin size="large" style={{ display: 'block', margin: '60px auto' }} />
      ) : (
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <PipelineTiles statusCounts={stats?.statusCounts} />
          </Col>
          <Col xs={24} xl={12}>
            <StageProgressChart stageTaskCounts={stats?.stageTaskCounts} />
          </Col>
          <Col xs={24} xl={12}>
            <NeedsAttentionCard bottlenecks={stats?.bottlenecks} />
          </Col>
          <Col xs={24} xl={12}>
            <PendingByStageRoleChart
              pendingTasksByStageRole={stats?.pendingTasksByStageRole}
            />
          </Col>
          <Col xs={24} xl={12}>
            <CompletedByPersonChart
              completedTasksByPersonMonth={stats?.completedTasksByPersonMonth}
            />
          </Col>
          <Col xs={24} xl={12}>
            <PartnerFlowPerQuarterChart
              quarterFlowCounts={stats?.quarterFlowCounts}
            />
          </Col>
          {/* The two "started vs completed" cuts sit side by side. */}
          <Col xs={24} xl={12}>
            <RegionFlowChart regionFlowCounts={stats?.regionFlowCounts} />
          </Col>
          <Col xs={24} xl={12}>
            <PendingByPersonTable
              pendingTasksByPerson={stats?.pendingTasksByPerson}
            />
          </Col>
          <Col xs={24} xl={12}>
            <PartnersByTypeCard partnerTypeCounts={stats?.partnerTypeCounts} />
          </Col>
        </Row>
      )}
    </CSPage>
  );
}

export default PartnerOnboardingDashboardPage;
